import React, { useState } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { chatbotUrl } from '../../../util/config';

const SAMPLE_PROMPTS = [
    'WhatsApp support bot that asks for customer name and ticket number, calls ticket status API, and offers to escalate to human agent',
    'Appointment booking bot that collects patient ID, preferred doctor, and appointment date',
    'Order tracking bot that asks for order ID and fetches delivery status from API',
    'Lead generation bot that asks for budget, location preference, and contact details',
    'HR onboarding bot that collects employee name, department, start date, and sends welcome info',
];

export default function GeneratePromptOverlay({ onClose, onResult }) {
    const [prompt,     setPrompt]     = useState('');
    const [generating, setGenerating] = useState(false);
    const [preview,    setPreview]    = useState(null);

    const generate = async () => {
        if (!prompt.trim()) { toast.error('Enter a prompt'); return; }
        setGenerating(true); setPreview(null);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${chatbotUrl}/flows/generate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ prompt: prompt.trim() }),
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data?.detail || 'Generation failed');
            const result = data?.data || data;
            setPreview(result);
            toast.success(`Generated ${result.node_count || result.metadata?.nodes?.length || 0} nodes!`);
        } catch (err) {
            toast.error(err.message || 'Generation failed');
        } finally {
            setGenerating(false);
        }
    };

    return (
        <div style={{ position:'fixed', inset:0, zIndex:100, display:'flex', alignItems:'center', justifyContent:'center', background:'rgba(0,0,0,0.6)' }}>
            <div style={{ background:'#fff', borderRadius:20, padding:32, maxWidth:680, width:'100%', margin:'0 16px', maxHeight:'90vh', overflowY:'auto', boxShadow:'0 24px 80px rgba(0,0,0,0.3)' }}>
                {/* Header */}
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:24 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                        <div style={{ width:40, height:40, borderRadius:10, background:'#eff6ff', display:'flex', alignItems:'center', justifyContent:'center' }}>
                            <i className="bi bi-cpu" style={{ fontSize:20, color:'#1565c0' }}></i>
                        </div>
                        <div>
                            <h3 style={{ fontSize:18, fontWeight:700, color:'#111827', margin:0 }}>Generate Flow with AI</h3>
                            <p style={{ fontSize:12, color:'#6b7280', margin:0 }}>Uses Groq — describe your WhatsApp bot in plain English</p>
                        </div>
                    </div>
                    <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:'#6b7280', fontSize:18 }}>×</button>
                </div>

                {/* Prompt */}
                <div style={{ marginBottom:16 }}>
                    <label style={lbl}>Describe your bot</label>
                    <textarea value={prompt} onChange={e => setPrompt(e.target.value)}
                        placeholder="e.g. WhatsApp support bot that asks for customer name and ticket number, checks ticket status via API, and escalates to human agent if needed..."
                        rows={4}
                        style={{ width:'100%', padding:'12px 14px', border:'1px solid #e5e7eb', borderRadius:12, fontSize:13, color:'#111827', resize:'vertical', outline:'none', boxSizing:'border-box', fontFamily:'inherit' }}/>
                </div>

                {/* Sample prompts */}
                <div style={{ marginBottom:20 }}>
                    <p style={lbl}>Sample Prompts</p>
                    <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                        {SAMPLE_PROMPTS.slice(0,3).map((p,i) => (
                            <button key={i} onClick={() => setPrompt(p)}
                                style={{ textAlign:'left', padding:'8px 12px', background:'#f8fafc', border:'1px solid #e5e7eb', borderRadius:8, fontSize:12, color:'#374151', cursor:'pointer' }}
                                onMouseEnter={e => { e.currentTarget.style.background='#eff6ff'; e.currentTarget.style.borderColor='#bfdbfe'; e.currentTarget.style.color='#1565c0'; }}
                                onMouseLeave={e => { e.currentTarget.style.background='#f8fafc'; e.currentTarget.style.borderColor='#e5e7eb'; e.currentTarget.style.color='#374151'; }}>
                                {p}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Generate button */}
                <button onClick={generate} disabled={generating || !prompt.trim()}
                    style={{ width:'100%', padding:'12px 24px', fontSize:14, fontWeight:700, color:'#fff', background: generating||!prompt.trim() ? '#9ca3af':'linear-gradient(to right, #2388e0, #1565c0)', border:'none', borderRadius:10, cursor: generating||!prompt.trim() ? 'not-allowed':'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:8, marginBottom:16 }}>
                    {generating ? (
                        <><span style={{ width:16, height:16, border:'2px solid rgba(255,255,255,0.3)', borderTopColor:'#fff', borderRadius:'50%', display:'inline-block', animation:'spin 0.8s linear infinite' }}></span> Generating...</>
                    ) : (
                        <><i className="bi bi-send"></i> Generate Flow</>
                    )}
                </button>

                {/* Preview */}
                {preview && (
                    <div style={{ background:'#f8fafc', border:'1px solid #e5e7eb', borderRadius:12, padding:16 }}>
                        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
                            <p style={{ fontSize:13, fontWeight:700, color:'#111827', margin:0 }}>
                                Generated: {preview.node_count || preview.metadata?.nodes?.length || 0} nodes
                            </p>
                            <button onClick={() => onResult(preview)}
                                style={{ padding:'8px 20px', fontSize:13, fontWeight:700, color:'#fff', background:'#16a34a', border:'none', borderRadius:8, cursor:'pointer' }}>
                                Use This Flow →
                            </button>
                        </div>
                        <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
                            {(preview.metadata?.nodes || []).map((n,i) => (
                                <span key={i} style={{ fontSize:11, padding:'3px 10px', background:'#eff6ff', color:'#1565c0', borderRadius:20, fontFamily:'monospace' }}>
                                    {n.type}
                                </span>
                            ))}
                        </div>
                    </div>
                )}
            </div>
            <ToastContainer/>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
    );
}

const lbl = { fontSize:11, fontWeight:700, color:'#6b7280', display:'block', marginBottom:8, textTransform:'uppercase', letterSpacing:'0.06em' };
